"use client"

/**
 * CRM Settings Tab for Messaging Agents
 *
 * Simplified version of email CRM settings:
 * - CRM integration selection (uses crm_bindings with scopeType: "message_agent")
 * - Connection status
 * - Opportunity routing: per-channel stages, AI-excluded contacts, human escalation
 *   (each destination has its own on/off switch)
 * - NO email sorting / category mapping (not applicable for messaging)
 * - NO create_note_on_reply (message agent uses session close notes, not per-reply notes)
 */

import { useEffect, useState, useCallback } from "react"
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card"
import { Switch } from "@/components/ui/switch"
import { Label } from "@/components/ui/label"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import {
  Link2,
  AlertCircle,
  AlertTriangle,
  UserX,
  CheckCircle2,
  XCircle,
  Unplug,
  Plus,
  Loader2,
} from "lucide-react"
import { cn } from "@/lib/utils"
import { useCrmBinding, type CrmStage } from "@/hooks/integrations/use-crm-binding"
import { useCrmIntegrations } from "@/hooks/integrations/use-crm-integrations"
import type { CrmProviderType, MessagingLeadPolicy } from "@/lib/crm/types"
import { AddCrmIntegrationDialog, CrmProviderLogo } from "@/components/crm"
import { HandoverPipelineSelector } from "@/components/agents/email/handover-pipeline-selector"
import { PageLoader } from "@/components/ui/page-loader"
import { usePermissions } from "@/hooks/use-permissions"
import { CRM_PERMISSIONS } from "@/lib/auth/permissions.constants"
import { Alert, AlertDescription } from "@/components/ui/alert"
import { useAuth } from "@/hooks/auth/use-auth"
import type { ChannelType, MessageAgentConfig } from "@/lib/agents/messaging/types"
import { CHANNEL_LABELS } from "@/lib/agents/messaging/types"
import { useTranslations } from "next-intl"
import { TranslationText } from "@/components/i18n/translation-text"






interface CrmSettingsProps {
  agentId: string
  canManage: boolean
  channels: Array<{ id: string; channel_type: string; account_name: string | null; status: string }>
}

export function CrmSettings({ agentId, canManage, channels }: CrmSettingsProps) {



  const tUi = useTranslations("Messaging.AgentsMessagingSettingsCrmSettings")

  const { fetchWithAuth } = useAuth()
  const { can } = usePermissions()

  const {
    binding,
    integrations,
    pipelines,
    selectedIntegration,
    isLoading,
    isLoadingPipelines,
    pipelinesError,
    isSaving,
    error,
    loadIntegrations,
    saveBinding,
    updateBindingConfig,
    getStagesForPipeline,
    hasTenantDefault,
    tenantDefaultIntegration,
  } = useCrmBinding({
    scopeType: "message_agent",
    scopeId: agentId,
    autoLoad: true,
  })

  const {
    providers,
    isConnecting,
    connectIntegration,
    prepareOAuthInstall,
    error: crmError,
    clearError: clearCrmError,
    refreshIntegrations: refreshCrmIntegrations,
  } = useCrmIntegrations({ autoLoad: true })

  // Agent config for handover_crm
  const [agentConfig, setAgentConfig] = useState<MessageAgentConfig>({})
  const [isLoadingConfig, setIsLoadingConfig] = useState(true)

  const [handoverStages, setHandoverStages] = useState<CrmStage[]>([])
  const [connectionTestResult, setConnectionTestResult] = useState<{
    success: boolean
    message: string
  } | null>(null)
  const [showAddCrmDialog, setShowAddCrmDialog] = useState(false)

  // Load agent config
  useEffect(() => {
    const load = async () => {
      try {
        const res = await fetchWithAuth(`/api/agents/messaging/${agentId}`)
        if (res.ok) {
          const data = await res.json()
          setAgentConfig(data.agent?.config ?? {})
        }
      } catch {
        // ignore — CRM tab still works without agent config
      } finally {
        setIsLoadingConfig(false)
      }
    }
    load()
    loadIntegrations()
  }, [fetchWithAuth, agentId, loadIntegrations])

  // Load handover stages
  useEffect(() => {
    if (binding?.config?.handover?.pipeline_id) {
      setHandoverStages(getStagesForPipeline(binding.config.handover.pipeline_id))
    } else {
      setHandoverStages([])
    }
  }, [binding?.config?.handover?.pipeline_id, pipelines, getStagesForPipeline])

  const isCrmConnected = !!binding
  const isCrmEnabled = isCrmConnected && binding.config?.enabled !== false
  const isSentimentEnabled = agentConfig.tools_enabled?.assess_sentiment === true
  // `chatAvailable` = adapter AND this environment's channel credentials. The provider
  // name alone would show the toggle, and auto-write the ON below, where pushes throw.
  const providerHasChat =
    !!selectedIntegration &&
    providers.find((p) => p.id === selectedIntegration.provider)?.chatAvailable === true
  const replyViaCrm = binding?.config?.messaging?.reply_via_crm
  const isReplyViaCrmEnabled = replyViaCrm !== false // undefined → treat as ON for the UI

  // First time the user lands on the settings page for an agent whose CRM integration supports
  // chat AND the binding has no explicit reply_via_crm value yet — write the implicit ON to the
  // SQL row so the server-side opt-in check (`messaging.reply_via_crm === true`) starts mirroring
  // immediately. Subsequent visits do nothing (`replyViaCrm` is set, so the condition is false).
  // Guard on canManage so viewers don't auto-mutate state.
  useEffect(() => {
    if (
      canManage &&
      isCrmEnabled &&
      providerHasChat &&
      replyViaCrm === undefined &&
      !isSaving
    ) {
      void updateBindingConfig({
        messaging: { ...binding?.config?.messaging, reply_via_crm: true },
      })
    }
  }, [
    canManage,
    isCrmEnabled,
    providerHasChat,
    replyViaCrm,
    isSaving,
    binding?.config?.messaging,
    updateBindingConfig,
  ])

  const handleIntegrationChange = async (integrationId: string) => {
    setConnectionTestResult(null)
    if (integrationId === "none") {
      if (binding) {
        // Disable without clearing pipeline config — user can re-enable later
        await updateBindingConfig({ enabled: false, explicitly_disabled: true })
      } else if (integrations.length > 0) {
        // Create binding to override tenant default with explicit opt-out
        await saveBinding(integrations[0].id, { enabled: false, explicitly_disabled: true })
      }
      return
    }
    // Switching to a different integration resets pipeline config;
    // re-enabling the same integration preserves it
    if (binding?.integrationId === integrationId) {
      await updateBindingConfig({ enabled: true, explicitly_disabled: false })
    } else {
      await saveBinding(integrationId, { enabled: true })
    }
  }

  const handleEnabledToggle = async (enabled: boolean) => {
    await updateBindingConfig({ enabled })
  }

  const handleReplyViaCrmToggle = async (enabled: boolean) => {
    await updateBindingConfig({
      messaging: { ...binding?.config?.messaging, reply_via_crm: enabled },
    })
  }

  const handleReuseOpenOpportunityToggle = async (enabled: boolean) => {
    await updateBindingConfig({
      messaging: { ...binding?.config?.messaging, reuse_open_opportunity: enabled },
    })
  }

  const handleLeadPolicyChange = async (policy: MessagingLeadPolicy) => {
    await updateBindingConfig({
      messaging: { ...binding?.config?.messaging, lead_policy: policy },
    })
  }

  const handleSentimentToggle = async (requireSentiment: boolean) => {
    await updateBindingConfig({
      messaging: {
        ...binding?.config?.messaging,
        require_sentiment: requireSentiment,
      },
    })
  }

  const handleChannelPipelineChange = async (channelType: string, pipelineId: string) => {
    const prev = binding?.config?.messaging?.channels ?? {}
    await updateBindingConfig({
      messaging: {
        ...binding?.config?.messaging,
        channels: { ...prev, [channelType]: { ...prev[channelType], pipeline_id: pipelineId, stage_id: undefined } },
      },
    })
  }

  const handleChannelStageChange = async (channelType: string, stageId: string) => {
    const prev = binding?.config?.messaging?.channels ?? {}
    await updateBindingConfig({
      messaging: {
        ...binding?.config?.messaging,
        channels: { ...prev, [channelType]: { ...prev[channelType], stage_id: stageId } },
      },
    })
  }

  const handleLeadsWhileAiOffToggle = async (enabled: boolean) => {
    await updateBindingConfig({
      messaging: { ...binding?.config?.messaging, leads_while_ai_off: enabled },
    })
  }

  const handleExclusionEnabledToggle = async (enabled: boolean) => {
    await updateBindingConfig({
      messaging: {
        ...binding?.config?.messaging,
        exclusion: { ...binding?.config?.messaging?.exclusion, enabled },
      },
    })
  }

  const handleExclusionPipelineChange = async (pipelineId: string) => {
    const prevEnabled = binding?.config?.messaging?.exclusion?.enabled
    if (pipelineId === "channel_default") {
      // Unset: shallow top-level merge replaces `messaging` wholesale, and
      // JSON.stringify drops the undefined key — the stage override is removed
      // while an explicit on/off choice survives.
      await updateBindingConfig({
        messaging: {
          ...binding?.config?.messaging,
          exclusion: prevEnabled === undefined ? undefined : { enabled: prevEnabled },
        },
      })
      return
    }
    await updateBindingConfig({
      messaging: {
        ...binding?.config?.messaging,
        exclusion: { enabled: prevEnabled, pipeline_id: pipelineId, stage_id: undefined },
      },
    })
  }

  const handleExclusionStageChange = async (stageId: string) => {
    await updateBindingConfig({
      messaging: {
        ...binding?.config?.messaging,
        exclusion: { ...binding?.config?.messaging?.exclusion, stage_id: stageId },
      },
    })
  }

  const handleHandoverEnabledToggle = async (enabled: boolean) => {
    await updateBindingConfig({
      handover: { ...binding?.config?.handover, enabled },
    })
  }

  const handleHandoverPipelineChange = async (pipelineId: string) => {
    await updateBindingConfig({
      handover: { ...binding?.config?.handover, pipeline_id: pipelineId, stage_id: undefined },
    })
  }

  const handleHandoverStatusChange = async (statusId: string) => {
    await updateBindingConfig({
      handover: { ...binding?.config?.handover, stage_id: statusId },
    })
  }

  const handleConnectCrm = useCallback(
    async (provider: string, name: string, credentials: Record<string, unknown>, metadata?: Record<string, unknown>) => {
      const result = await connectIntegration(provider as CrmProviderType, name, credentials, metadata)
      if (result.success) {
        await loadIntegrations()
        await refreshCrmIntegrations()
      }
      return result
    },
    [connectIntegration, loadIntegrations, refreshCrmIntegrations]
  )

  const getProviderInfo = useCallback(
    (providerId: string) => providers.find((p) => p.id === providerId),
    [providers]
  )

  const getProviderDisplayName = (provider: string) => {
    const names: Record<string, string> = { kommo: "Kommo", hubspot: "HubSpot", salesforce: "Salesforce", pipedrive: "Pipedrive" }
    return names[provider] || provider
  }

  // Reuse must never write into a finished deal, so it is only offered for
  // providers whose adapter can report closed state.
  const providerReportsClosedState = selectedIntegration
    ? getProviderInfo(selectedIntegration.provider)?.capabilities?.reportsClosedState === true
    : false

  const terminology = selectedIntegration ? getProviderInfo(selectedIntegration.provider)?.terminology : null
  const t = terminology || { opportunity: "opportunity", opportunityPlural: "opportunities", pipeline: "pipeline", stage: "stage" }
  const capitalize = (s: string) => s.charAt(0).toUpperCase() + s.slice(1)

  const bindingConfig = binding?.config || {}
  const channelConfigs = bindingConfig.messaging?.channels ?? {}
  const connectedChannels = channels.filter((c) => c.status === "connected")

  const leadPolicy: MessagingLeadPolicy = bindingConfig.messaging?.lead_policy ?? "pii_required"

  // Both CRM destinations default to on — absent config means "as before".
  const exclusionConfig = bindingConfig.messaging?.exclusion ?? {}
  const isExclusionCrmEnabled = exclusionConfig.enabled !== false
  const isHandoverCrmEnabled = bindingConfig.handover?.enabled !== false
  const exclusionStages = exclusionConfig.pipeline_id
    ? getStagesForPipeline(exclusionConfig.pipeline_id).filter((s) => !s.isArchived)
    : []

  if (isLoading || isLoadingConfig) {
    return <PageLoader message={tUi("loadingCRMSettings")} />
  }

  if (!can(CRM_PERMISSIONS.VIEW)) {
    return (
      <Card>
        <CardHeader><CardTitle><span key={tUi("accessDenied")}>{tUi("accessDenied")}</span></CardTitle></CardHeader>
        <CardContent>
          <Alert>
            <AlertDescription><span key={tUi("youDonTHavePermissionToAccessCRM")}>{tUi("youDonTHavePermissionToAccessCRM")}</span></AlertDescription>
          </Alert>
        </CardContent>
      </Card>
    )
  }

  return (
    <div className="space-y-6">
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">{error}</div>
      )}

      {/* CRM Connection */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Link2 className="w-5 h-5" />
            <span><span key={tUi("cRMIntegration")}>{tUi("cRMIntegration")}</span></span>
          </CardTitle>
          <CardDescription>
            <span key={tUi("connectThisAgentToYourCRMToCreate")}>{tUi("connectThisAgentToYourCRMToCreate")}</span></CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {integrations.length === 0 ? (
            <div className="space-y-4">
              <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                <div className="flex items-center gap-2">
                  <AlertCircle className="w-4 h-4 text-yellow-600" />
                  <span className="text-sm text-yellow-800">
                    <span key={tUi("noCRMIntegrationsAvailableConnectACRMTo")}>{tUi("noCRMIntegrationsAvailableConnectACRMTo")}</span></span>
                </div>
              </div>
              {canManage && (
                <Button variant="outline" onClick={() => setShowAddCrmDialog(true)}>
                  <Plus className="w-4 h-4 mr-2" />
                  <span><span key={tUi("connectCRM")}>{tUi("connectCRM")}</span></span>
                </Button>
              )}
            </div>
          ) : (
            <div className="space-y-4">
              {/* Integration selector */}
              <div className="space-y-2">
                <Label className="text-sm font-medium"><span key={tUi("cRMConnection")}>{tUi("cRMConnection")}</span></Label>
                <Select
                  value={binding?.config?.explicitly_disabled ? "none" : (binding?.integrationId || "")}
                  onValueChange={handleIntegrationChange}
                  disabled={isSaving || !canManage}
                >
                  <SelectTrigger>
                    <SelectValue placeholder={tUi("selectACRMIntegration")} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">
                      <div className="flex items-center gap-2">
                        <Unplug className="w-4 h-4 text-gray-400" />
                        <span className="text-gray-500"><span key={tUi("noneDisabled")}>{tUi("noneDisabled")}</span></span>
                      </div>
                    </SelectItem>
                    {integrations.map((integration) => {
                      const providerInfo = getProviderInfo(integration.provider)
                      return (
                        <SelectItem key={integration.id} value={integration.id}>
                          <div className="flex items-center gap-2">
                            {providerInfo?.logoUrl ? (
                              <CrmProviderLogo provider={integration.provider} logoUrl={providerInfo.logoUrl} size={16} />
                            ) : (
                              <div className={cn("w-4 h-4 rounded-full", integration.isActive ? "bg-green-500" : "bg-gray-300")} />
                            )}
                            <span>
                              {integration.name}{" "}
                              <span className="text-gray-400">({getProviderDisplayName(integration.provider)})</span>
                            </span>
                            {!integration.isActive && (
                              <Badge variant="secondary" className="text-xs ml-1"><span key={tUi("disabled")}>{tUi("disabled")}</span></Badge>
                            )}
                          </div>
                        </SelectItem>
                      )
                    })}
                  </SelectContent>
                </Select>
              </div>

              {/* Enable/disable lead creation toggle (visible when integration selected) */}
              {isCrmConnected && !binding?.config?.explicitly_disabled && (
                <div className="flex items-center gap-3 p-3 border rounded-lg">
                  <Switch
                    id="crm-enabled"
                    checked={isCrmEnabled}
                    onCheckedChange={handleEnabledToggle}
                    disabled={isSaving || !canManage}
                  />
                  <Label htmlFor="crm-enabled" className="text-sm cursor-pointer">
                    <span><span key={tUi("create")}>{tUi("create")}</span></span> {t.opportunityPlural.toLowerCase()} <span><span key={tUi("fromConversations")}>{tUi("fromConversations")}</span></span>
                  </Label>
                </div>
              )}

              {/* Mirror conversations into the CRM chat thread (per-agent, chat-capable providers only) */}
              {isCrmEnabled && providerHasChat && (
                <div className="flex items-start gap-3 p-3 border rounded-lg">
                  <Switch
                    id="crm-reply-via-chat"
                    checked={isReplyViaCrmEnabled}
                    onCheckedChange={handleReplyViaCrmToggle}
                    disabled={isSaving || !canManage}
                    className="mt-0.5"
                  />
                  <Label htmlFor="crm-reply-via-chat" className="text-sm cursor-pointer flex-1">
                    <div><span key={tUi("mirrorConversationsToCRMChat")}>{tUi("mirrorConversationsToCRMChat")}</span></div>
                    <div className="text-xs text-muted-foreground mt-0.5">
                      <span key={tUi("sendCustomerAIAndOperatorMessagesIntoThe")}>{tUi("sendCustomerAIAndOperatorMessagesIntoThe")}</span></div>
                  </Label>
                </div>
              )}

              {/* Enabled where it cannot run: hiding the toggle would leave no way off. */}
              {isCrmEnabled && !providerHasChat && replyViaCrm === true && (
                <div className="flex items-start gap-3 p-3 border border-amber-300 bg-amber-50 rounded-lg">
                  <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" />
                  <div className="flex-1">
                    <div className="text-sm font-medium text-amber-900">
                      <span key={tUi("cRMChatMirroringIsEnabledButUnavailable")}>{tUi("cRMChatMirroringIsEnabledButUnavailable")}</span></div>
                    <div className="text-xs text-amber-800 mt-0.5">
                      <span key={tUi("thisAgentIsSetToMirrorConversationsInto")}>{tUi("thisAgentIsSetToMirrorConversationsInto")}</span></div>
                    {canManage && (
                      <Button
                        variant="outline"
                        size="sm"
                        className="mt-2"
                        disabled={isSaving}
                        onClick={() => handleReplyViaCrmToggle(false)}
                      >
                        <span key={tUi("turnOffMirroring")}>{tUi("turnOffMirroring")}</span></Button>
                    )}
                  </div>
                </div>
              )}

              {canManage && (
                <Button variant="outline" size="sm" onClick={() => setShowAddCrmDialog(true)}>
                  <Plus className="w-4 h-4 mr-2" />
                  <span><span key={tUi("orAddADifferentCRM")}>{tUi("orAddADifferentCRM")}</span></span>
                </Button>
              )}

              {isCrmEnabled && selectedIntegration && !pipelinesError && (
                <div className="flex items-center gap-2 p-3 bg-green-50 border border-green-200 rounded-lg">
                  <CheckCircle2 className="w-4 h-4 text-green-600" />
                  <span className="text-sm text-green-800"><TranslationText>{tUi.rich("connectedToValue0Value1", { value0: String(selectedIntegration.name), value1: String(getProviderDisplayName(selectedIntegration.provider)) })}</TranslationText></span>
                </div>
              )}

              {pipelinesError && (
                <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg">
                  <XCircle className="w-4 h-4 text-red-600" />
                  <span className="text-sm text-red-800">{pipelinesError}</span>
                </div>
              )}

              {!binding && hasTenantDefault && tenantDefaultIntegration && (
                <div className="flex items-center gap-2 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                  <AlertCircle className="w-4 h-4 text-blue-600" />
                  <span className="text-sm text-blue-800"><TranslationText>{tUi.rich("usingTenantDefaultValue0Value1SelectASpecific", { value0: String(tenantDefaultIntegration.name), value1: String(getProviderDisplayName(tenantDefaultIntegration.provider)) })}</TranslationText></span>
                </div>
              )}

              {connectionTestResult && (
                <div className={cn(
                  "flex items-center gap-2 p-3 rounded-lg",
                  connectionTestResult.success ? "bg-green-50 border border-green-200" : "bg-red-50 border border-red-200"
                )}>
                  {connectionTestResult.success
                    ? <CheckCircle2 className="w-4 h-4 text-green-600" />
                    : <XCircle className="w-4 h-4 text-red-600" />}
                  <span className={cn("text-sm", connectionTestResult.success ? "text-green-800" : "text-red-800")}>
                    {connectionTestResult.message}
                  </span>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Opportunity routing — channels, excluded contacts, human escalation */}
      {isCrmEnabled && selectedIntegration && (
        <Card>
          <CardHeader>
            <CardTitle>{capitalize(t.opportunity)} <span><span key={tUi("routing")}>{tUi("routing")}</span></span></CardTitle>
            <CardDescription>
              <span><span key={tUi("where")}>{tUi("where")}</span></span> {t.opportunityPlural.toLowerCase()} <span><span key={tUi("areCreatedAndMoved")}>{tUi("areCreatedAndMoved")}</span></span>
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {isLoadingPipelines ? (
              <div className="flex items-center gap-2 p-2">
                <Loader2 className="w-4 h-4 animate-spin" />
                <span className="text-sm text-gray-500"><TranslationText>{tUi.rich("loadingValue0S", { value0: String(t.pipeline) })}</TranslationText></span>
              </div>
            ) : (
              <>
                {connectedChannels.length > 0 && (
                  <div className="space-y-4">
                    <div>
                      <Label className="text-sm font-medium"><span key={tUi("channels")}>{tUi("channels")}</span></Label>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        <span><span key={tUi("whereNew")}>{tUi("whereNew")}</span></span> {t.opportunityPlural.toLowerCase()} <span><span key={tUi("areCreatedForEachChannel")}>{tUi("areCreatedForEachChannel")}</span></span>
                      </p>
                    </div>

                    {connectedChannels.map((ch) => {
                      const chConf = channelConfigs[ch.channel_type] ?? {}
                      const chStages = chConf.pipeline_id
                        ? getStagesForPipeline(chConf.pipeline_id).filter((s) => !s.isArchived)
                        : []

                      return (
                        <div key={ch.id} className="space-y-3">
                          <Label className="text-sm font-normal text-muted-foreground">
                            {CHANNEL_LABELS[ch.channel_type as ChannelType] || ch.channel_type}
                            {ch.account_name && <span> — {ch.account_name}</span>}
                          </Label>
                          <div className="grid grid-cols-2 gap-4">
                            <Select
                              value={chConf.pipeline_id || ""}
                              onValueChange={(v) => handleChannelPipelineChange(ch.channel_type, v)}
                              disabled={isSaving || !canManage}
                            >
                              <SelectTrigger>
                                <SelectValue placeholder={tUi("value0", { value0: String(capitalize(t.pipeline)) })} />
                              </SelectTrigger>
                              <SelectContent>
                                {pipelines.map((p) => (
                                  <SelectItem key={p.id} value={p.id}>
                                    {p.name} <span>{p.isDefault && "(Default)"}</span>
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            <Select
                              value={chConf.stage_id || ""}
                              onValueChange={(v) => handleChannelStageChange(ch.channel_type, v)}
                              disabled={!chConf.pipeline_id || isSaving || !canManage}
                            >
                              <SelectTrigger>
                                <SelectValue placeholder={tUi("value0", { value0: String(capitalize(t.stage)) })} />
                              </SelectTrigger>
                              <SelectContent>
                                {chStages.map((s) => (
                                  <SelectItem key={s.id} value={s.id}>
                                    <div className="flex items-center gap-2">
                                      <div className="w-3 h-3 rounded-full" style={{ backgroundColor: s.color || "#9ca3af" }} />
                                      <span>{s.name}</span>
                                    </div>
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                        </div>
                      )
                    })}

                    {connectedChannels.some((ch) => !channelConfigs[ch.channel_type]?.pipeline_id) && (
                      <div className="flex items-center gap-2 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                        <AlertCircle className="w-4 h-4 text-yellow-600" />
                        <span className="text-sm text-yellow-800"><TranslationText>{tUi.rich("selectValue0AndValue1ForEachChannelTo", { value0: String(t.pipeline), value1: String(t.stage), value2: String(t.opportunityPlural.toLowerCase()) })}</TranslationText></span>
                      </div>
                    )}
                  </div>
                )}

                {/* AI-excluded contacts: the one flow with no LLM turn, so its stage is config-only */}
                <div className="space-y-3 border-t pt-4">
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <Label className="text-sm font-medium"><span key={tUi("aIExcludedContacts")}>{tUi("aIExcludedContacts")}</span></Label>
                      <p key={isExclusionCrmEnabled
                          ? tUi("contactsTheAINeverAnswersLeaveTheValue0", { value0: String(t.stage.toLowerCase()), value1: String(t.stage.toLowerCase()) })
                          : tUi("excludedContactsCreateNoValue0TheirConversationsStill", { value0: String(t.opportunityPlural.toLowerCase()) })} className="text-xs text-muted-foreground mt-0.5">
                        {isExclusionCrmEnabled
                          ? tUi("contactsTheAINeverAnswersLeaveTheValue0", { value0: String(t.stage.toLowerCase()), value1: String(t.stage.toLowerCase()) })
                          : tUi("excludedContactsCreateNoValue0TheirConversationsStill", { value0: String(t.opportunityPlural.toLowerCase()) })}
                      </p>
                    </div>
                    <Switch
                      checked={isExclusionCrmEnabled}
                      onCheckedChange={handleExclusionEnabledToggle}
                      disabled={isSaving || !canManage}
                    />
                  </div>
                  {isExclusionCrmEnabled && (
                    <div className="grid grid-cols-2 gap-4">
                      <Select
                        value={exclusionConfig.pipeline_id || "channel_default"}
                        onValueChange={handleExclusionPipelineChange}
                        disabled={isSaving || !canManage}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder={tUi("value0", { value0: String(capitalize(t.pipeline)) })} />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="channel_default">
                            <span className="text-gray-500"><TranslationText>{tUi.rich("useChannelValue0Default", { value0: String(t.stage.toLowerCase()) })}</TranslationText></span>
                          </SelectItem>
                          {pipelines.map((p) => (
                            <SelectItem key={p.id} value={p.id}>
                              {p.name} <span>{p.isDefault && "(Default)"}</span>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Select
                        value={exclusionConfig.stage_id || ""}
                        onValueChange={handleExclusionStageChange}
                        disabled={!exclusionConfig.pipeline_id || isSaving || !canManage}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder={tUi("value0", { value0: String(capitalize(t.stage)) })} />
                        </SelectTrigger>
                        <SelectContent>
                          {exclusionStages.map((s) => (
                            <SelectItem key={s.id} value={s.id}>
                              <div className="flex items-center gap-2">
                                <div className="w-3 h-3 rounded-full" style={{ backgroundColor: s.color || "#9ca3af" }} />
                                <span>{s.name}</span>
                              </div>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                </div>

                {/* Channels whose AI replies are switched off (channel toggle) */}
                <div className="space-y-3 border-t pt-4">
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <Label className="text-sm font-medium"><span key={tUi("channelsWithAIRepliesOff")}>{tUi("channelsWithAIRepliesOff")}</span></Label>
                      <p key={bindingConfig.messaging?.leads_while_ai_off === true
                          ? tUi("newConversationsOnSuchChannelsStillCreateValue0", { value0: String(t.opportunityPlural.toLowerCase()) })
                          : tUi("noNewValue0IsCreatedOnSuchChannels", { value0: String(t.opportunity.toLowerCase()) })} className="text-xs text-muted-foreground mt-0.5">
                        {bindingConfig.messaging?.leads_while_ai_off === true
                          ? tUi("newConversationsOnSuchChannelsStillCreateValue0", { value0: String(t.opportunityPlural.toLowerCase()) })
                          : tUi("noNewValue0IsCreatedOnSuchChannels", { value0: String(t.opportunity.toLowerCase()) })}
                      </p>
                    </div>
                    <Switch
                      checked={bindingConfig.messaging?.leads_while_ai_off === true}
                      onCheckedChange={handleLeadsWhileAiOffToggle}
                      disabled={isSaving || !canManage}
                    />
                  </div>
                </div>

                {/* Human escalation — only meaningful when the tool can fire */}
                {agentConfig.tools_enabled?.human_handover === true && (
                  <div className="space-y-3 border-t pt-4">
                    <div className="flex items-start justify-between gap-4">
                      <div>
                        <Label className="text-sm font-medium flex items-center gap-2">
                          <UserX className="w-4 h-4 text-red-500" />
                          <span><span key={tUi("humanEscalation")}>{tUi("humanEscalation")}</span></span>
                        </Label>
                        <p key={isHandoverCrmEnabled
                            ? tUi("whereTheValue0GoesWhenTheAIEscalates", { value0: String(t.opportunity.toLowerCase()) })
                            : tUi("escalationsDoNotTouchCrm")} className="text-xs text-muted-foreground mt-0.5">
                          {isHandoverCrmEnabled
                            ? tUi("whereTheValue0GoesWhenTheAIEscalates", { value0: String(t.opportunity.toLowerCase()) })
                            : tUi("escalationsDoNotTouchCrm")}
                        </p>
                      </div>
                      <Switch
                        checked={isHandoverCrmEnabled}
                        onCheckedChange={handleHandoverEnabledToggle}
                        disabled={isSaving || !canManage}
                      />
                    </div>
                    {isHandoverCrmEnabled && (
                      <HandoverPipelineSelector
                        layout="row"
                        pipelines={pipelines}
                        stages={handoverStages}
                        selectedPipelineId={bindingConfig.handover?.pipeline_id}
                        selectedStageId={bindingConfig.handover?.stage_id}
                        isLoadingPipelines={isLoadingPipelines}
                        isSaving={isSaving}
                        showWarning
                        onPipelineChange={handleHandoverPipelineChange}
                        onStageChange={handleHandoverStatusChange}
                      />
                    )}
                  </div>
                )}
              </>
            )}
          </CardContent>
        </Card>
      )}

      {/* Session lead policy — every condition that gates an ordinary conversation's lead */}
      {isCrmEnabled && selectedIntegration && (
        <Card>
          <CardHeader>
            <CardTitle><span><span key={tUi("session")}>{tUi("session")}</span></span> {t.opportunity.toLowerCase()} <span><span key={tUi("policy")}>{tUi("policy")}</span></span></CardTitle>
            <CardDescription>
              <span><span key={tUi("chooseWhenAnOrdinaryConversationCreatesA")}>{tUi("chooseWhenAnOrdinaryConversationCreatesA")}</span></span> {t.opportunity.toLowerCase()}<span>.</span>
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-2">
            <Select
              value={leadPolicy}
              onValueChange={(v) => handleLeadPolicyChange(v as MessagingLeadPolicy)}
              disabled={isSaving || !canManage}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="pii_required"><span key={tUi("whenContactInfoIsCapturedDefault")}>{tUi("whenContactInfoIsCapturedDefault")}</span></SelectItem>
                <SelectItem value="always"><span key={tUi("everySession")}>{tUi("everySession")}</span></SelectItem>
                <SelectItem value="never"><span key={tUi("never")}>{tUi("never")}</span></SelectItem>
              </SelectContent>
            </Select>
            <p className="text-xs text-gray-500">
              {(() => {
                switch (leadPolicy) {
                  case "pii_required":
                    return `A ${t.opportunity.toLowerCase()} is created once the conversation captures an email or phone number (phone-identified channels such as WhatsApp qualify immediately).`
                  case "always":
                    return `Every session creates a ${t.opportunity.toLowerCase()} on the first AI reply — including a casual "hi" that goes nowhere, so expect name-only ${t.opportunityPlural.toLowerCase()} that need manual sorting.`
                  case "never":
                    return `Sessions never create ${t.opportunityPlural.toLowerCase()}; conversation notes and the CRM chat mirror stay off.`
                }
              })()}
            </p>
            {leadPolicy !== "never" && (
            <div className="flex items-start justify-between gap-4 border-t pt-4">
              <div>
                <Label htmlFor="sentiment-toggle" className="text-sm font-medium cursor-pointer">
                  <span key={tUi("skipConversationsTheAIResolved")}>{tUi("skipConversationsTheAIResolved")}</span></Label>
                <p key={isSentimentEnabled
                    ? tUi("aConversationTheAIClosesSuccessfullyCreatesNo", { value0: String(t.opportunity.toLowerCase()) })
                    : tUi("needsTheSentimentAnalysisToolEnabledInThe")} className="text-xs text-muted-foreground mt-0.5">
                  {isSentimentEnabled
                    ? tUi("aConversationTheAIClosesSuccessfullyCreatesNo", { value0: String(t.opportunity.toLowerCase()) })
                    : tUi("needsTheSentimentAnalysisToolEnabledInThe")}
                </p>
              </div>
              <Switch
                id="sentiment-toggle"
                checked={bindingConfig.messaging?.require_sentiment || false}
                onCheckedChange={handleSentimentToggle}
                disabled={isSaving || !canManage || !isSentimentEnabled}
              />
            </div>
            )}

            <p className="text-xs text-muted-foreground">
              <span><span key={tUi("appliesToOrdinaryConversationsOnlyEscalationsAndAI")}>{tUi("appliesToOrdinaryConversationsOnlyEscalationsAndAI")}</span></span> {t.opportunityPlural.toLowerCase()} <span><span key={tUi("whateverThisIsSetToEachIsSwitched")}>{tUi("whateverThisIsSetToEachIsSwitched")}</span></span> {t.opportunity.toLowerCase()} <span><span key={tUi("routingAbove")}>{tUi("routingAbove")}</span></span>
            </p>
          </CardContent>
        </Card>
      )}

      {/* Returning visitors */}
      {isCrmEnabled && selectedIntegration && (
        <Card>
          <CardHeader>
            <CardTitle><span key={tUi("returningVisitors")}>{tUi("returningVisitors")}</span></CardTitle>
            <CardDescription>
              <span><span key={tUi("controlWhetherANewConversationSessionOpensA")}>{tUi("controlWhetherANewConversationSessionOpensA")}</span></span> {t.opportunity.toLowerCase()}<span>.</span>
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between p-4 border rounded-lg bg-blue-50 border-blue-200">
              <div>
                <Label htmlFor="reuse-opportunity-toggle" className="text-sm font-medium text-gray-700 cursor-pointer">
                  <span><span key={tUi("keepWritingIntoTheOpen")}>{tUi("keepWritingIntoTheOpen")}</span></span> {t.opportunity.toLowerCase()}
                </Label>
                <p key={providerReportsClosedState
                    ? tUi("whenAVisitorReturnsTheNewSessionContinues", { value0: String(t.opportunity.toLowerCase()), value1: String(t.opportunity.toLowerCase()), value2: String(t.opportunityPlural.toLowerCase()) })
                    : tUi("value0DoesNotReportWhetherAValue1Is", { value0: String(getProviderDisplayName(selectedIntegration.provider)), value1: String(t.opportunity.toLowerCase()) })} className="text-xs text-gray-500 mt-1">
                  {providerReportsClosedState
                    ? tUi("whenAVisitorReturnsTheNewSessionContinues", { value0: String(t.opportunity.toLowerCase()), value1: String(t.opportunity.toLowerCase()), value2: String(t.opportunityPlural.toLowerCase()) })
                    : tUi("value0DoesNotReportWhetherAValue1Is", { value0: String(getProviderDisplayName(selectedIntegration.provider)), value1: String(t.opportunity.toLowerCase()) })}
                </p>
              </div>
              <Switch
                id="reuse-opportunity-toggle"
                checked={bindingConfig.messaging?.reuse_open_opportunity === true}
                onCheckedChange={handleReuseOpenOpportunityToggle}
                disabled={isSaving || !canManage || !providerReportsClosedState}
              />
            </div>
          </CardContent>
        </Card>
      )}

      {/* Add CRM Dialog */}
      <AddCrmIntegrationDialog
        open={showAddCrmDialog}
        onOpenChange={setShowAddCrmDialog}
        providers={providers}
        onConnect={handleConnectCrm}
        onPrepareOAuth={prepareOAuthInstall}
        isConnecting={isConnecting}
        error={crmError}
        onClearError={clearCrmError}
      />
    </div>
  )
}
